import { create } from 'zustand'
import notificationService from './notificationService'

function sortNotifications(items) {
  return [...items].sort((a, b) => {
    const left = new Date(a?.createdAt || a?.created_at || 0).getTime()
    const right = new Date(b?.createdAt || b?.created_at || 0).getTime()
    return right - left
  })
}

function toNotificationRecord(input) {
  if (!input) return null
  return {
    id: input.id || input.notificationId || null,
    dedupeKey: input.dedupeKey || null,
    type: input.type || 'notification',
    channel: input.channel || 'system',
    severity: input.severity || 'info',
    title: input.title || 'Notification',
    message: input.message || '',
    actionUrl: input.actionUrl || null,
    status: input.status || 'unread',
    createdAt: input.createdAt || input.created_at || new Date().toISOString(),
    readAt: input.readAt || input.read_at || null,
  }
}

function upsertNotification(list, nextRaw) {
  const next = toNotificationRecord(nextRaw)
  if (!next) return list

  const index = list.findIndex((item) => {
    if (next.id && item.id) return item.id === next.id
    if (next.dedupeKey && item.dedupeKey) return item.dedupeKey === next.dedupeKey
    return false
  })

  if (index < 0) {
    return sortNotifications([next, ...list])
  }

  const clone = [...list]
  clone[index] = { ...clone[index], ...next }
  return sortNotifications(clone)
}

function buildToast(input) {
  return {
    id: input.id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: input.kind || 'notification',
    severity: input.severity || 'info',
    title: input.title || 'Notification',
    message: input.message || '',
    operationId: input.operationId || null,
    progressCurrent: input.progressCurrent ?? null,
    progressTotal: input.progressTotal ?? null,
    persistent: Boolean(input.persistent),
    createdAt: input.createdAt || new Date().toISOString(),
  }
}

function upsertToast(list, next) {
  if (!next?.id) return list
  const index = list.findIndex((item) => item.id === next.id)
  if (index < 0) return [next, ...list]
  const clone = [...list]
  clone[index] = { ...clone[index], ...next }
  return clone
}

function computeUnreadCount(notifications) {
  return notifications.filter((row) => row.status === 'unread').length
}

const initialState = {
  notifications: [],
  unreadCount: 0,
  toasts: [],
  banners: [],
  operations: {},
  realtimeStatus: 'idle',
  seenEventIds: new Set(),
}

export const useNotificationStore = create((set, get) => ({
  ...initialState,

  resetActorState: () => set({
    ...initialState,
    realtimeStatus: 'idle',
  }),

  setRealtimeStatus: (status) => set({ realtimeStatus: status || 'idle' }),

  loadInitialNotifications: async () => {
    const [notifications, unreadCount] = await Promise.all([
      notificationService.listNotifications({ status: 'all', limit: 100 }),
      notificationService.getUnreadCount(),
    ])

    const normalized = sortNotifications(
      (notifications || [])
        .map((row) => toNotificationRecord(row))
        .filter(Boolean),
    )

    set({
      notifications: normalized,
      unreadCount: Number(unreadCount || computeUnreadCount(normalized)),
    })
  },

  addOrUpdateNotification: (notification) => set((state) => {
    const notifications = upsertNotification(state.notifications, notification)
    return {
      notifications,
      unreadCount: computeUnreadCount(notifications),
    }
  }),

  markRead: async (id) => {
    await notificationService.markNotificationRead(id)
    set((state) => {
      const notifications = state.notifications.map((item) => (
        item.id === id ? { ...item, status: 'read', readAt: item.readAt || new Date().toISOString() } : item
      ))
      return {
        notifications,
        unreadCount: computeUnreadCount(notifications),
      }
    })
  },

  markAllRead: async () => {
    await notificationService.markAllNotificationsRead()
    set((state) => {
      const notifications = state.notifications.map((item) => ({ ...item, status: 'read', readAt: item.readAt || new Date().toISOString() }))
      return {
        notifications,
        unreadCount: 0,
      }
    })
  },

  archive: async (id) => {
    await notificationService.archiveNotification(id)
    set((state) => {
      const notifications = state.notifications.map((item) => (
        item.id === id ? { ...item, status: 'archived' } : item
      ))
      return {
        notifications,
        unreadCount: computeUnreadCount(notifications),
      }
    })
  },

  deleteNotification: async (id) => {
    await notificationService.deleteNotification(id)
    set((state) => {
      const notifications = state.notifications.filter((item) => item.id !== id)
      return {
        notifications,
        unreadCount: computeUnreadCount(notifications),
      }
    })
  },

  showToast: (toast) => set((state) => {
    const next = buildToast(toast)
    return { toasts: upsertToast(state.toasts, next) }
  }),

  dismissToast: (id) => set((state) => ({
    toasts: state.toasts.filter((toast) => toast.id !== id),
  })),

  upsertOperation: (operation) => set((state) => {
    if (!operation?.id) return state
    return {
      operations: {
        ...state.operations,
        [operation.id]: {
          ...state.operations[operation.id],
          ...operation,
        },
      },
    }
  }),

  removeBanner: (id) => set((state) => ({
    banners: state.banners.filter((banner) => banner.id !== id),
  })),

  handleRealtimeEvent: (event) => {
    if (!event?.id) return

    const state = get()
    if (state.seenEventIds.has(event.id)) return

    const seenEventIds = new Set(state.seenEventIds)
    seenEventIds.add(event.id)
    if (seenEventIds.size > 2000) {
      const first = seenEventIds.values().next().value
      if (first) seenEventIds.delete(first)
    }

    let notifications = state.notifications
    let toasts = state.toasts
    let banners = state.banners
    let operations = state.operations

    const notificationFromEvent = event.notification
      ? {
          id: event.notification.id,
          type: event.type,
          severity: event.notification.severity,
          title: event.notification.title,
          message: event.notification.message,
          actionUrl: event.notification.actionUrl,
          status: 'unread',
          createdAt: event.createdAt,
        }
      : null

    if (event.type.startsWith('mobile.')) {
      set({ seenEventIds })
      return
    }

    if (event.type.startsWith('operation.') && event.operation?.id) {
      operations = {
        ...operations,
        [event.operation.id]: {
          ...operations[event.operation.id],
          ...event.operation,
          updatedAt: event.createdAt,
        },
      }

      const progressToastId = `operation-progress-${event.operation.id}`

      if (event.type === 'operation.started' || event.type === 'operation.progress') {
        toasts = upsertToast(toasts, buildToast({
          id: progressToastId,
          kind: 'operation-progress',
          severity: 'info',
          title: notificationFromEvent?.title || event.operation.type || 'Operation in progress',
          message: notificationFromEvent?.message || 'Background operation is running',
          progressCurrent: event.operation.progressCurrent,
          progressTotal: event.operation.progressTotal,
          operationId: event.operation.id,
          persistent: true,
          createdAt: event.createdAt,
        }))
      }

      if (event.type === 'operation.completed' || event.type === 'operation.failed' || event.type === 'operation.cancelled') {
        toasts = toasts.filter((toast) => toast.id !== progressToastId)
        if (notificationFromEvent) {
          const toastSeverity = event.type === 'operation.completed'
            ? 'success'
            : event.type === 'operation.failed'
              ? 'error'
              : 'warning'
          toasts = upsertToast(toasts, buildToast({
            id: `terminal-${notificationFromEvent.id || event.id}`,
            kind: 'notification',
            severity: toastSeverity,
            title: notificationFromEvent.title,
            message: notificationFromEvent.message,
            persistent: toastSeverity === 'error',
            createdAt: event.createdAt,
          }))
        }
      }
    }

    if (event.type === 'notification.created' || event.type === 'notification.updated') {
      if (notificationFromEvent) {
        notifications = upsertNotification(notifications, notificationFromEvent)
      }
      if (event.type === 'notification.created' && notificationFromEvent) {
        toasts = upsertToast(toasts, buildToast({
          id: `notif-toast-${notificationFromEvent.id || event.id}`,
          severity: notificationFromEvent.severity,
          title: notificationFromEvent.title,
          message: notificationFromEvent.message,
          persistent: notificationFromEvent.severity === 'error' || notificationFromEvent.severity === 'warning',
          createdAt: event.createdAt,
        }))
      }
    }

    if (event.type === 'queue.degraded') {
      const bannerId = 'queue-degraded'
      if (!banners.some((banner) => banner.id === bannerId)) {
        banners = [
          {
            id: bannerId,
            severity: 'warning',
            title: notificationFromEvent?.title || 'Queue degradation detected',
            message: notificationFromEvent?.message || 'Queue processing is degraded. Some jobs may be delayed.',
          },
          ...banners,
        ]
      }
      if (notificationFromEvent) {
        notifications = upsertNotification(notifications, notificationFromEvent)
      }
    }

    if (event.type === 'queue.recovered') {
      banners = banners.filter((banner) => banner.id !== 'queue-degraded')
      if (notificationFromEvent) {
        notifications = upsertNotification(notifications, notificationFromEvent)
        toasts = upsertToast(toasts, buildToast({
          id: `queue-recovered-${notificationFromEvent.id || event.id}`,
          severity: 'success',
          title: notificationFromEvent.title,
          message: notificationFromEvent.message,
          createdAt: event.createdAt,
        }))
      }
    }

    set({
      seenEventIds,
      notifications,
      unreadCount: computeUnreadCount(notifications),
      toasts,
      banners,
      operations,
    })
  },
}))

export default useNotificationStore
